/* ── SiteMenusEditor ──────────────────────────────────────
 *
 * The Menus section of the website settings: a website's NAMED navigation
 * menus (`functional_specs.menus` on the External Website node — header,
 * footer, any other). A `nav_menu` block shows one with `menu: <name>`.
 *
 * Menus are SITE data: where a page sits in the navigation tree does not
 * decide which menu shows it, and one page can be in several menus. Each
 * entry is a page reference (its URL and label come from the page, so they
 * never go stale), a custom link, or a dropdown holding more entries.
 *
 * Also hosts the one-time "Convert to menus" for a site still on the old
 * "Default Header" / "Default Footer" folder convention.
 */

import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, FileText, GripVertical, Link2, Plus, Trash2, Wand2 } from 'lucide-react'
import type { SiteMenuEntry, SiteMenusSpec } from '@twasta/page-schema'

export interface MenuPageOption {
  obj_name: string
  title: string
  /** A collection template (one page per record) — never a menu destination. */
  template?: boolean
}

const MENU_NAME_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/

const fieldStyle: React.CSSProperties = {
  padding: '0.35rem 0.55rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
  background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: '0.875rem', minWidth: 0,
}
const iconBtn: React.CSSProperties = { padding: '0 4px', minWidth: 0 }
const muted: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.45 }

type Path = number[]

function getList(entries: SiteMenuEntry[], path: Path): SiteMenuEntry[] {
  // path addresses a LIST: [] = top level, [i] = children of entry i, …
  let list = entries
  for (const i of path) list = list[i]?.children || []
  return list
}

function setList(entries: SiteMenuEntry[], path: Path, next: SiteMenuEntry[]): SiteMenuEntry[] {
  if (!path.length) return next
  const [head, ...rest] = path
  return entries.map((e, i) => (i === head ? { ...e, children: setList(e.children || [], rest, next) } : e))
}

function isPrefix(a: Path, b: Path): boolean {
  return a.length <= b.length && a.every((v, i) => b[i] === v)
}

/** Remove the entry at `listPath`/`index`; returns [entries, removed]. */
function takeEntry(entries: SiteMenuEntry[], listPath: Path, index: number): [SiteMenuEntry[], SiteMenuEntry | null] {
  const list = getList(entries, listPath)
  const removed = list[index] ?? null
  return [setList(entries, listPath, list.filter((_, i) => i !== index)), removed]
}

/** Strip every reference to the given pages from every site's menus (in
 *  memory) — used when pages are deleted from the tree. Mirrors the backend
 *  `site_menus.strip_removed_pages`. */
export function stripPagesFromMenus(menus: SiteMenusSpec, objNames: Set<string>): SiteMenusSpec | null {
  let changed = false
  const strip = (entries: SiteMenuEntry[]): SiteMenuEntry[] => {
    const out: SiteMenuEntry[] = []
    for (const e of entries) {
      if (e.page && objNames.has(e.page)) { changed = true; continue }
      if (e.children && !e.page) {
        const kids = strip(e.children)
        if (!kids.length) { changed = true; continue }
        out.push(kids === e.children ? e : { ...e, children: kids })
      } else out.push(e)
    }
    return out
  }
  const next: SiteMenusSpec = {}
  for (const [name, entries] of Object.entries(menus || {})) next[name] = strip(entries || [])
  return changed ? next : null
}

// One list of entries (top level of a menu, or a dropdown's children). A
// MODULE-level component: defined inside the editor it would be a new
// component on every render and every text field would lose focus per key.
interface ListCtx {
  setMenu: (name: string, entries: SiteMenuEntry[]) => void
  drag: DragState | null
  setDrag: (d: DragState | null) => void
  pageTitle: Map<string, string>
  destinations: MenuPageOption[]
}
type DragState = { menu: string; listPath: Path; index: number }

function EntryList({ menu, entries, listPath, depth, ctx }: {
  menu: string; entries: SiteMenuEntry[]; listPath: Path; depth: number; ctx: ListCtx
}) {
    const { setMenu, drag, setDrag, pageTitle, destinations } = ctx
    const list = getList(entries, listPath)
    const update = (next: SiteMenuEntry[]) => setMenu(menu, setList(entries, listPath, next))
    const patch = (i: number, p: Partial<SiteMenuEntry>) => {
      const next = list.map((e, j) => {
        if (j !== i) return e
        const merged: SiteMenuEntry = { ...e, ...p }
        if (!merged.label) delete merged.label
        return merged
      })
      update(next)
    }
    const move = (i: number, dir: -1 | 1) => {
      const j = i + dir
      if (j < 0 || j >= list.length) return
      const next = list.slice()
      ;[next[i], next[j]] = [next[j], next[i]]
      update(next)
    }
    // Drop the dragged entry before row `i` of THIS list (or at the end).
    const dropAt = (i: number) => {
      if (!drag || drag.menu !== menu) return
      const from = [...drag.listPath, drag.index]
      // A dropdown cannot be dropped into itself.
      if (isPrefix(from, listPath)) return
      let [without, moved] = takeEntry(entries, drag.listPath, drag.index)
      if (!moved) return
      // Removing an earlier sibling shifts this list's indices.
      let target = i
      if (drag.listPath.length === listPath.length && drag.listPath.every((v, k) => v === listPath[k]) && drag.index < i) target -= 1
      // Removing an entry that precedes an ANCESTOR of this list shifts the path.
      let path = listPath
      if (drag.listPath.length < listPath.length && isPrefix(drag.listPath, listPath)
          && drag.index < listPath[drag.listPath.length]) {
        path = listPath.slice()
        path[drag.listPath.length] -= 1
      }
      const dest = getList(without, path).slice()
      dest.splice(Math.max(0, Math.min(target, dest.length)), 0, moved)
      without = setList(without, path, dest)
      setMenu(menu, without)
      setDrag(null)
    }
    const addPage = (obj: string) => obj && update([...list, { page: obj }])

    return (
      <div style={{ display: 'grid', gap: 6 }}>
        {list.map((e, i) => {
          const kind: 'page' | 'link' | 'group' = e.page ? 'page' : (e.children && !e.href) ? 'group' : 'link'
          const missing = kind === 'page' && !pageTitle.has(e.page || '')
          return (
            <div
              key={`${i}:${e.page || e.href || e.label || ''}`}
              onDragOver={(ev) => { if (drag?.menu === menu) ev.preventDefault() }}
              onDrop={(ev) => { ev.preventDefault(); ev.stopPropagation(); dropAt(i) }}
              style={{
                border: '1px solid var(--color-border)', borderRadius: 6, padding: '6px 8px',
                background: depth ? 'var(--color-bg)' : 'var(--color-bg-secondary)', display: 'grid', gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  draggable
                  onDragStart={() => setDrag({ menu, listPath, index: i })}
                  onDragEnd={() => setDrag(null)}
                  title="Drag to reorder or move into a dropdown"
                  style={{ cursor: 'grab', color: 'var(--color-text-secondary)', display: 'inline-flex' }}
                >
                  <GripVertical size={14} />
                </span>
                {kind === 'page' && <FileText size={14} style={{ flexShrink: 0 }} />}
                {kind === 'link' && <Link2 size={14} style={{ flexShrink: 0 }} />}
                {kind === 'group' && <ChevronDown size={14} style={{ flexShrink: 0 }} />}
                {kind === 'page' ? (
                  <>
                    <select
                      value={e.page}
                      onChange={(ev) => patch(i, { page: ev.target.value })}
                      style={{ ...fieldStyle, flex: '1 1 40%' }}
                    >
                      {missing && <option value={e.page}>{e.page} (page not found)</option>}
                      {destinations.map((p) => (
                        <option key={p.obj_name} value={p.obj_name}>{p.title || p.obj_name}</option>
                      ))}
                    </select>
                    <input
                      value={e.label || ''}
                      onChange={(ev) => patch(i, { label: ev.target.value })}
                      placeholder={pageTitle.get(e.page || '') || 'Label'}
                      title="Menu label (leave empty to use the page's own)"
                      style={{ ...fieldStyle, flex: '1 1 30%' }}
                    />
                  </>
                ) : (
                  <input
                    value={e.label || ''}
                    onChange={(ev) => patch(i, { label: ev.target.value })}
                    placeholder={kind === 'group' ? 'Dropdown label' : 'Label'}
                    style={{ ...fieldStyle, flex: '1 1 30%' }}
                  />
                )}
                {kind === 'link' && (
                  <input
                    value={e.href || ''}
                    onChange={(ev) => patch(i, { href: ev.target.value })}
                    placeholder="/path or https://…"
                    style={{ ...fieldStyle, flex: '1 1 40%' }}
                  />
                )}
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 2, flexShrink: 0 }}>
                  <button type="button" className="btn btn-ghost btn-sm" style={iconBtn} disabled={i === 0} onClick={() => move(i, -1)} title="Move up"><ArrowUp size={12} /></button>
                  <button type="button" className="btn btn-ghost btn-sm" style={iconBtn} disabled={i === list.length - 1} onClick={() => move(i, 1)} title="Move down"><ArrowDown size={12} /></button>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ ...iconBtn, color: 'var(--color-error)' }} onClick={() => update(list.filter((_, j) => j !== i))} title="Remove from menu"><Trash2 size={12} /></button>
                </span>
              </div>
              {missing && (
                <span style={{ ...muted, color: 'var(--color-error)' }}>
                  This page no longer exists — the entry is not shown on the site.
                </span>
              )}
              {kind === 'group' && (
                <div style={{ borderLeft: '2px solid var(--color-border)', paddingLeft: 8, marginLeft: 4 }}>
                  <EntryList menu={menu} entries={entries} listPath={[...listPath, i]} depth={depth + 1} ctx={ctx} />
                </div>
              )}
            </div>
          )
        })}
        {/* End-of-list drop zone + add buttons. */}
        <div
          onDragOver={(ev) => { if (drag?.menu === menu) ev.preventDefault() }}
          onDrop={(ev) => { ev.preventDefault(); ev.stopPropagation(); dropAt(list.length) }}
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', minHeight: 24 }}
        >
          <select
            value=""
            onChange={(ev) => addPage(ev.target.value)}
            style={{ ...fieldStyle, width: 'auto' }}
            title="Add a page of this site"
          >
            <option value="">+ Page…</option>
            {destinations.map((p) => (
              <option key={p.obj_name} value={p.obj_name}>{p.title || p.obj_name}</option>
            ))}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => update([...list, { label: 'New link', href: '' }])}>
            <Plus size={12} /> Link
          </button>
          {depth < 3 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => update([...list, { label: 'New dropdown', children: [] }])}>
              <Plus size={12} /> Dropdown
            </button>
          )}
          {!list.length && (
            <span style={muted}>{depth ? 'Empty dropdowns are not shown.' : 'No links yet.'}</span>
          )}
        </div>
      </div>
    )
}

export default function SiteMenusEditor({
  menus,
  pages,
  onChange,
  legacy,
}: {
  menus: SiteMenusSpec | undefined
  pages: MenuPageOption[]
  onChange: (next: SiteMenusSpec | undefined) => void
  legacy?: {
    /** The site still decides menus by folder position. */
    active: boolean
    busy: boolean
    /** Why converting is not possible right now (e.g. unsaved edits). */
    disabledReason?: string
    onConvert: () => void
  }
}) {
  const spec = menus || {}
  const names = Object.keys(spec)
  const [newName, setNewName] = useState('')
  const [drag, setDrag] = useState<DragState | null>(null)
  const pageTitle = useMemo(() => new Map(pages.map((p) => [p.obj_name, p.title || p.obj_name])), [pages])
  const destinations = useMemo(() => pages.filter((p) => !p.template), [pages])

  const setMenu = (name: string, entries: SiteMenuEntry[]) => onChange({ ...spec, [name]: entries })
  const addMenu = (name: string) => {
    if (!MENU_NAME_RE.test(name) || name in spec) return
    onChange({ ...spec, [name]: [] })
    setNewName('')
  }
  const removeMenu = (name: string) => {
    const next = { ...spec }
    delete next[name]
    onChange(Object.keys(next).length ? next : undefined)
  }
  const renameMenu = (from: string, to: string) => {
    if (!MENU_NAME_RE.test(to) || to in spec) return
    // Rebuild in order so the card does not jump to the end.
    const next: SiteMenusSpec = {}
    for (const [k, v] of Object.entries(spec)) next[k === from ? to : k] = v
    onChange(next)
  }

  const ctx: ListCtx = { setMenu, drag, setDrag, pageTitle, destinations }

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      <p style={{ ...muted, margin: 0 }}>
        The links your header and footer show. A <code>nav_menu</code> block picks one by name
        (<code>header</code>, <code>footer</code> …). Where a page sits in the navigation tree does not
        affect any menu, and a page can be in several menus.
      </p>

      {legacy?.active && (
        <div style={{
          border: '1px solid var(--color-warning, #d97706)', borderRadius: 8, padding: '0.6rem 0.75rem',
          display: 'grid', gap: 6, background: 'color-mix(in srgb, var(--color-warning, #d97706) 8%, transparent)',
        }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>This site still uses menu folders</span>
          <span style={muted}>
            Its menus come from where pages sit in the tree (the “Default Header” / “Default Footer”
            folders). Converting turns those folders into menus here, moves their pages up to the website
            itself and points the header/footer menu blocks at the new menus. The landing page stays the same.
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={legacy.onConvert}
              disabled={legacy.busy || !!legacy.disabledReason}
              title={legacy.disabledReason}
            >
              <Wand2 size={12} /> {legacy.busy ? 'Converting…' : 'Convert to menus'}
            </button>
            {legacy.disabledReason && <span style={muted}>{legacy.disabledReason}</span>}
          </span>
        </div>
      )}

      {names.map((name) => (
        <div key={name} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '0.6rem 0.7rem', display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MenuNameInput name={name} taken={names} onRename={(to) => renameMenu(name, to)} />
            <span style={muted}>{name === 'header' ? 'Header menu' : name === 'footer' ? 'Footer menu' : 'Menu'}</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: 'auto', color: 'var(--color-error)' }}
              onClick={() => removeMenu(name)}
              title="Delete this menu"
            >
              <Trash2 size={12} />
            </button>
          </div>
          <EntryList menu={name} entries={spec[name] || []} listPath={[]} depth={0} ctx={ctx} />
          {!(spec[name] || []).length && destinations.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ justifySelf: 'start' }}
              onClick={() => setMenu(name, destinations.map((p) => ({ page: p.obj_name })))}
            >
              Add every page
            </button>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {!('header' in spec) && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => addMenu('header')}>
            <Plus size={12} /> Header menu
          </button>
        )}
        {!('footer' in spec) && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => addMenu('footer')}>
            <Plus size={12} /> Footer menu
          </button>
        )}
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-'))}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMenu(newName) } }}
          placeholder="another-menu"
          style={{ ...fieldStyle, width: 150 }}
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!MENU_NAME_RE.test(newName) || newName in spec}
          onClick={() => addMenu(newName)}
        >
          <Plus size={12} /> New menu
        </button>
      </div>
    </div>
  )
}

// Menu name, committed on blur/Enter so a half-typed name never renames the
// menu (and never collides with another one mid-edit).
function MenuNameInput({ name, taken, onRename }: {
  name: string; taken: string[]; onRename: (to: string) => void
}) {
  const [draft, setDraft] = useState(name)
  const valid = MENU_NAME_RE.test(draft) && (draft === name || !taken.includes(draft))
  const commit = () => {
    if (draft !== name && valid) onRename(draft)
    else setDraft(name)
  }
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-'))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit() } }}
      title="Menu name — what a nav_menu block's Menu setting refers to"
      style={{ ...fieldStyle, width: 140, fontWeight: 600, borderColor: valid ? 'var(--color-border)' : 'var(--color-error)' }}
    />
  )
}
